import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { resetPassword, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center gap-2 p-4 border border-red-200 bg-red-50 mb-6 rounded-lg">
          <WarningCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600">
            Invalid or missing reset token.{" "}
            <Link to="/forgot-password" className="underline">Request a new link</Link>
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Password updated successfully");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-green-50 border border-green-200 mb-6 rounded-xl">
          <CheckCircle size={24} className="text-green-600" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Success
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          Password updated.
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Your password has been changed. Redirecting you to sign in…
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-blue-600 hover:underline"
        >
          Sign in now <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    );
  }

  const inputBase = "w-full bg-slate-50 border focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900";

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        / Reset password
      </span>
      <h1 className="font-bold text-4xl mt-3 mb-2 text-slate-900">
        Choose a new password.
      </h1>
      <p className="text-sm text-slate-500 mb-8">
        Enter a new password below. Min 6 characters.
      </p>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            New password
          </label>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${inputBase} border-slate-200 focus:border-blue-500`}
            autoComplete="new-password"
            data-testid="reset-password-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Confirm new password
          </label>
          <input
            type="password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={`${inputBase} ${
              confirm && confirm !== password
                ? "border-red-300 focus:border-red-400"
                : "border-slate-200 focus:border-blue-500"
            }`}
            autoComplete="new-password"
            data-testid="reset-confirm-input"
          />
          {confirm && confirm !== password && (
            <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
          )}
        </div>

        {err && (
          <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-600 rounded-lg">
            {err}
            {err.toLowerCase().includes("expired") && (
              <span>
                {" "}
                <Link to="/forgot-password" className="underline text-red-500">
                  Request a new link
                </Link>
              </span>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || (confirm.length > 0 && confirm !== password)}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 text-sm rounded-lg transition-colors inline-flex items-center justify-center gap-2"
          data-testid="reset-submit-button"
        >
          {busy ? "Updating…" : "Update password"} <ArrowRight size={14} weight="bold" />
        </button>
      </form>
    </div>
  );
}
